import { useNavigate } from "react-router-dom";
import useMerchantPortalAxios from "../../utils/axios";
import { SESSION_STORAGE_KEYS } from "../constants/sessionStorage";

export default function useAiApi() {
  const client = useMerchantPortalAxios();
  const navigate = useNavigate();

  const getSid = () =>
    sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID) ||
    sessionStorage.getItem("SessionID") ||
    "";

  const getToken = () => {
    const auth =
      client.defaults.headers?.common?.Authorization ||
      client.defaults.headers?.Authorization ||
      "";

    if (auth) return auth.replace("Bearer ", "");

    for (const key of [
      SESSION_STORAGE_KEYS.TOKEN,
      "token",
      "access_token",
      "accessToken",
      "jwt",
      "authToken",
      "bearerToken",
      "id_token",
    ]) {
      const value = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (value) return value;
    }

    return "";
  };

  const baseUrl = client.defaults.baseURL || "";

  function AnalyzeStream(prompt) {
    return fetch(`${baseUrl}/v1/Navigator/AnalyzeStream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ SessionID: getSid(), Prompt: prompt }),
    });
  }

  function VoiceAnalyze(audioBlob) {
    const formData = new FormData();
    formData.append("audio", audioBlob, "voice.webm");
    formData.append("SessionID", getSid());

    return fetch(`${baseUrl}/v1/Navigator/VoiceAnalyze`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    });
  }

  async function VoiceChat(flowSessionId, audioBlob) {
    const formData = new FormData();
    formData.append("audio", audioBlob, "voice.webm");
    formData.append("FlowSessionId", flowSessionId);
    formData.append("SessionID", getSid());

    const response = await client.post("/v1/Navigator/VoiceChat", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async function Chat(flowSessionId, userMessage) {
    const response = await client.post("/v1/Navigator/Chat", {
      SessionID: getSid(),
      FlowSessionId: flowSessionId,
      UserMessage: userMessage,
    });
    return response.data;
  }

  async function SaveApiResponse(flowSessionId, responseJson) {
    const response = await client.post("/v1/Navigator/SaveApiResponse", {
      SessionID: getSid(),
      FlowSessionId: flowSessionId,
      ResponseJson: responseJson,
    });
    return response.data;
  }

  async function ComputeHash(flowSessionId) {
    const response = await client.post("/v1/Navigator/ComputeHash", {
      SessionID: getSid(),
      FlowSessionId: flowSessionId,
    });
    return response.data;
  }

  async function DetectFile(file) {
    const formData = new FormData();
    formData.append("SessionID", getSid());
    formData.append("File", file, file?.name || "upload");

    const response = await client.post("/v1/Navigator/DetectFile", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async function UploadFile(flowSessionId, file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("flowSessionId", flowSessionId);
    formData.append("sessionID", getSid());

    const response = await client.post("/v1/Navigator/UploadFile", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async function PortalCall(endpoint, body, isMultipart = false, method = "POST") {
    try {
      const requestMethod = String(method || "POST").toUpperCase();

      const requestConfig = {
        url: endpoint,
        method: requestMethod,
      };

      if (isMultipart) {
        requestConfig.data = body;
        requestConfig.headers = { "Content-Type": "multipart/form-data" };
      } else if (requestMethod === "GET") {
        requestConfig.params = body;
      } else {
        requestConfig.data = body;
      }

      const response = await client.request(requestConfig);
      return response.data;
    } catch (error) {
      if (error.response?.data) return error.response.data;
      return {
        isPopup: true,
        PopupTitle: "Error",
        PopupDescription: error.message || "Network error",
      };
    }
  }

  async function AnalyzePrompt(prompt) {
    try {
      const response = await client.post("/v1/Navigator/AnalyzePrompt", {
        SessionID: getSid(),
        Prompt: prompt,
      });
      const data = response.data;
      if (data.Found === true && data.Path) {
        navigate(data.Path);
        return { success: true };
      }
      return { success: false, message: data.Message };
    } catch (error) {
      return { success: false, message: "System error. Please try again later." };
    }
  }

  async function GetWelcomeDashboard() {
    const response = await client.post("/v1/Navigator/GetWelcomeDashboard", {
      SessionID: getSid(),
    });
    return response.data;
  }

  return {
    AnalyzePrompt,
    AnalyzeStream,
    VoiceAnalyze,
    VoiceChat,
    Chat,
    SaveApiResponse,
    ComputeHash,
    DetectFile,
    UploadFile,
    PortalCall,
    GetWelcomeDashboard,
    getSid,
  };
}
